import { CHAIN_ID } from '@derivation-tech/context';
import { JsonRpcProvider } from '@ethersproject/providers';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalModalType, OPERATION_TX_TYPE } from '@/constants';
import { useAppDispatch } from '@/hooks';
import { useGa } from '@/hooks/useGa';
import { useTxNotification } from '@/hooks/useTxNotification';
import { ITransactionInfo } from '@/types/transaction';
import { mapObjectValue, parseTransactionNotification } from '@/utils/notification';

import { shortenAddress } from '@/utils/address';
import { getContractError } from '@/utils/error';
import { setOpenModal } from '../global/actions';
import { useGlobalModalType } from '../global/hooks';
import { finalizeTransaction } from './actions';
export function useShowConfirmedTxNotify(
  provider: JsonRpcProvider | undefined,
  userAddr: string | undefined,
): ({ chainId, hash, transaction }: { chainId: CHAIN_ID; hash: string; transaction: ITransactionInfo }) => void {
  const { open } = useTxNotification();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gaEvent = useGa();
  const closeModalAfterTx = useCloseModalAfterTx();

  return useCallback(
    async ({ chainId, hash, transaction }: { chainId: CHAIN_ID; hash: string; transaction: ITransactionInfo }) => {
      const { message, success } = await parseTransactionNotification({
        chainId,
        hash,
        transaction,
        provider,
        userAddr,
        t,
        gaEvent,
      });
      console.log('🚀 ~ useShowConfirmedTxNotify ~ message:', { message, success, transaction });

      // TODO: use correct notification template
      open({
        message: t(`notification.${transaction.type}.${success ? 'success' : 'error'}`),
        description: message,
        tx: transaction.txHash,
        taskId: transaction.taskId,
        type: success ? 'success' : 'error',
      });

      userAddr &&
        transaction.receipt &&
        dispatch(
          finalizeTransaction({
            chainId,
            traderAddr: userAddr,
            hash,
            receipt: transaction.receipt,
          }),
        );
      closeModalAfterTx(transaction);

      // display in recent tx list

      // clear up pending tx

      // dispatch display in pair history
    },
    [open, t, userAddr, dispatch, closeModalAfterTx, gaEvent, provider],
  );
}

export function useSendTxNotify(): ({
  chainId,
  hash,
  transaction,
}: {
  chainId: CHAIN_ID;
  hash: string;
  transaction: ITransactionInfo;
}) => void {
  // const dispatch = useAppDispatch();
  const { open } = useTxNotification();
  const { t } = useTranslation();
  return useCallback(
    ({ transaction }: { chainId: CHAIN_ID; hash: string; transaction: ITransactionInfo }) => {
      const title = t(`notification.${transaction.type}.info`);
      const desc = transaction.sendingTemplate
        ? t(
            transaction.sendingTemplate?.descTemplate,
            mapObjectValue(transaction.sendingTemplate?.templateArgs, 'string', (value) => t(value)),
          )
        : undefined;
      open({
        message: title,
        description: desc,
        tx: transaction.txHash,
        type: 'info',
        taskId: transaction.taskId,
      });
      console.record('tx', `[${transaction.type}] operation tx [${shortenAddress(transaction.txHash)}] sending `, {
        transaction,
        desc,
        title,
      });
    },
    [open, t],
  );
}

export function useSendTxError(): ({ error, txType }: { error: string; txType: OPERATION_TX_TYPE }) => void {
  // const dispatch = useAppDispatch();
  const { open } = useTxNotification();
  const { t } = useTranslation();
  return useCallback(
    ({ error, txType }: { error: string; txType: OPERATION_TX_TYPE }) => {
      if (error) {
        let errorContent = t(`notification.${txType}.error`);
        const mappingError = getContractError(error);
        if (mappingError) {
          errorContent = mappingError;
        } else {
          // TODO: remove temp code
          errorContent = `${errorContent}: ${error}`;
        }
        open({
          message: t(`notification.${txType}.error`),
          description: errorContent,
          type: 'error',
        });
      }
    },
    [open, t],
  );
}

/**
 * close global modal after send tx
 * @returns
 */
function useCloseModalAfterTx(): (txInfo: ITransactionInfo) => void {
  const modalType = useGlobalModalType();
  const dispatch = useAppDispatch();

  return useCallback(
    (txInfo: ITransactionInfo) => {
      let closeModal = false;
      if (modalType) {
        if (txInfo.type === OPERATION_TX_TYPE.DEPOSIT && modalType === GlobalModalType.Deposit) {
          closeModal = true;
        } else if (txInfo.type === OPERATION_TX_TYPE.WITHDRAW && modalType === GlobalModalType.Withdraw) {
          closeModal = true;
        }

        if (closeModal) {
          dispatch(setOpenModal({ type: null }));
        }
      }
    },
    [dispatch, modalType],
  );
}
